import { createContext, useContext, useState, useRef, useCallback, useEffect } from "react";
import { getAvailabilityReport } from "../lib/api/report";
import { useAuth } from "./AuthContext";
import { useUsers } from "./UserContext";

interface AvailabilityState {
  availabilityData: Record<string, { am: boolean; pm: boolean }>;
  isLoadingAvailability: boolean;
  loadedYears: Set<number>;
  loadAvailabilityForYear: (year: number) => Promise<void>;
  refreshAvailability: () => Promise<void>;
  clearAvailabilityCache: () => void;
}

const AvailabilityContext = createContext<AvailabilityState>({
  availabilityData: {},
  isLoadingAvailability: false,
  loadedYears: new Set(),
  loadAvailabilityForYear: async () => {},
  refreshAvailability: async () => {},
  clearAvailabilityCache: () => {},
});

export function AvailabilityProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const { currentUser, emit } = useUsers();
  const [availabilityData, setAvailabilityData] = useState<Record<string, { am: boolean; pm: boolean }>>({});
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  
  // Use ref to track loaded years to prevent race conditions
  const loadedYearsRef = useRef<Set<number>>(new Set());
  const loadingYearsRef = useRef<Set<number>>(new Set());

  // Function to load availability data for a specific year
  const loadAvailabilityForYear = useCallback(async (year: number) => {
    if (!token || !currentUser) {
      return;
    }

    // Check if already loaded or currently loading
    if (loadedYearsRef.current.has(year) || loadingYearsRef.current.has(year)) {
      return;
    }

    // Mark as loading
    loadingYearsRef.current.add(year);
    setIsLoadingAvailability(true);

    try {
      const report = await getAvailabilityReport(
        token,
        currentUser.site,
        currentUser.id,
        year.toString()
      );

      // Merge new availability data with existing data
      setAvailabilityData(prev => ({
        ...prev,
        ...report.availability
      }));

      // Mark this year as loaded
      loadedYearsRef.current.add(year);
    } catch (error) {
      console.error(`[loadAvailabilityForYear] Failed to load availability data for year ${year}:`, error);
    } finally {
      // Remove from loading set
      loadingYearsRef.current.delete(year);
      setIsLoadingAvailability(loadingYearsRef.current.size > 0);
    }
  }, [token, currentUser]);

  // Refresh all previously loaded years
  const refreshAvailability = useCallback(async () => {
    if (!token || !currentUser) return;

    const yearsToReload = Array.from(loadedYearsRef.current);
    if (yearsToReload.length === 0) return;

    setIsLoadingAvailability(true);

    try {
      const availabilityPromises = yearsToReload.map(year =>
        getAvailabilityReport(
          token,
          currentUser.site,
          currentUser.id,
          year.toString()
        )
      );

      const reports = await Promise.all(availabilityPromises);

      // Merge all availability data
      const mergedAvailability = reports.reduce((acc, report) => ({
        ...acc,
        ...report.availability
      }), {});

      setAvailabilityData(mergedAvailability);
      
      // Emit availability changed event
      emit("availabilityChanged", { availability: mergedAvailability });
    } catch (err) {
      console.error("Failed to refresh availability data:", err);
    } finally {
      setIsLoadingAvailability(false);
    }
  }, [token, currentUser]);

  // Clear cache (for logout or site switch)
  const clearAvailabilityCache = useCallback(() => {
    setAvailabilityData({});
    loadedYearsRef.current.clear();
    loadingYearsRef.current.clear();
    setIsLoadingAvailability(false);
  }, []);

  // Load current year on mount
  useEffect(() => {
    if (token && currentUser) {
      const currentYear = new Date().getFullYear();
      loadAvailabilityForYear(currentYear);
    } else {
      clearAvailabilityCache();
    }
  }, [token, currentUser, loadAvailabilityForYear, clearAvailabilityCache]);

  return (
    <AvailabilityContext.Provider
      value={{
        availabilityData,
        isLoadingAvailability,
        loadedYears: loadedYearsRef.current,
        loadAvailabilityForYear,
        refreshAvailability,
        clearAvailabilityCache,
      }}
    >
      {children}
    </AvailabilityContext.Provider>
  );
}

export function useAvailability() {
  const context = useContext(AvailabilityContext);
  if (!context) {
    throw new Error("useAvailability must be used within an AvailabilityProvider");
  }
  return context;
}
